/**
 * ChestWallProgramCard — iter1060 — 4-седмична програма за pectoralis и
 * гръдна екстензия, с измерване на болката преди и след всяка сесия.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Activity, AlertTriangle, ArrowRight, Check, ChevronDown, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const OUTCOME_TONE = {
  working: { light: "bg-emerald-50 border-emerald-300", dark: "bg-emerald-900/25 border-emerald-500/40" },
  partial: { light: "bg-sky-50 border-sky-200", dark: "bg-sky-900/20 border-sky-500/30" },
  early: { light: "bg-slate-50 border-slate-200", dark: "bg-slate-800/40 border-slate-700" },
  no_data: { light: "bg-slate-50 border-slate-200", dark: "bg-slate-800/40 border-slate-700" },
  no_change: { light: "bg-amber-50 border-amber-300", dark: "bg-amber-900/25 border-amber-500/40" },
  worse: { light: "bg-rose-50 border-rose-300", dark: "bg-rose-900/25 border-rose-500/40" },
};

export const ChestWallProgramCard = ({ isDark }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState(null);
  const [sess, setSess] = useState({ before: "", after: "" });

  const load = () => axios.get(`${API}/chest-wall/program`)
    .then(({ data }) => setData(data)).catch(() => setData(null))
    .finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [API]);

  const post = async (path, body, msg) => {
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${API}/chest-wall/${path}`, body);
      setData(d);
      if (msg) toast.success(msg);
      return d;
    } catch { toast.error("Действието не беше изпълнено."); }
    finally { setBusy(false); }
  };

  const logSession = async () => {
    if (sess.before === "" || sess.after === "") {
      toast.error("Нужни са и двете стойности — преди и след.");
      return;
    }
    await post("session", {
      pain_before: parseFloat(sess.before), pain_after: parseFloat(sess.after),
    }, "Сесията е записана.");
    setSess({ before: "", after: "" });
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const box = isDark ? "bg-slate-800/50" : "bg-slate-100";
  const inputCls = `px-2 py-1.5 rounded-md border text-sm w-full ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`;

  if (loading) {
    return <div className={`p-5 rounded-2xl border ${cardCls} flex justify-center`} data-testid="chest-wall-loading">
      <Loader2 className="w-5 h-5 animate-spin text-teal-500" /></div>;
  }
  if (!data) return null;
  const out = data.outcome || {};
  const tone = OUTCOME_TONE[out.id] || OUTCOME_TONE.no_data;
  const chartData = (data.sessions || []).map((s, i) => ({
    i: i + 1, date: s.date, преди: s.pain_before, след: s.pain_after,
  }));

  return (
    <div className={`p-5 rounded-2xl border-2 ${cardCls}`} data-testid="chest-wall-program-card">
      <button onClick={() => setOpen(!open)} className="w-full text-left flex items-center gap-2"
              data-testid="chest-wall-toggle">
        <Activity className="w-4 h-4 text-teal-500 shrink-0" />
        <h3 className="font-bold text-sm flex-1 min-w-0">Програма за гръдната стена</h3>
        {data.enrolled && (
          <span className="text-[10px] px-2 py-1 rounded-md font-bold bg-teal-600 text-white shrink-0 whitespace-nowrap"
                data-testid="chest-wall-week-badge">
            Седмица {data.current_week}
          </span>
        )}
        <ChevronDown className={`w-4 h-4 opacity-50 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {!data.enrolled && (
        <p className="text-[11px] opacity-75 leading-relaxed mt-1" data-testid="chest-wall-intro">
          {data.intro_bg}
        </p>
      )}
      {data.offer_reason_bg && (
        <p className="text-[10px] opacity-70 leading-relaxed mt-1" data-testid="chest-wall-offer">
          {data.offer_reason_bg}
        </p>
      )}
      {data.red_flag && (
        <div className={`mt-2 p-3 rounded-xl border ${isDark ? "bg-rose-900/30 border-rose-500/50" : "bg-rose-50 border-rose-300"}`}
             data-testid="chest-wall-red-flag">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-rose-500" />
            <span className="text-xs font-bold">Първо преглед</span>
          </div>
          <div className="text-[11px] opacity-90 mt-1 leading-relaxed">
            В раздела има отбелязан предупредителен признак. Програмата не се започва, докато лекар не е видял находката.
          </div>
        </div>
      )}

      {data.enrolled && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]" data-testid="chest-wall-progress">
          <span className={`px-2 py-1 rounded-md ${box}`}>
            Ден {data.day} от 28
          </span>
          <span className={`px-2 py-1 rounded-md ${box}`}>
            Изпълнение {data.progress.percent}% ({data.progress.done}/{data.progress.total})
          </span>
          <span className={`px-2 py-1 rounded-md ${box}`} data-testid="chest-wall-streak">
            Серия {data.streak} дни
          </span>
        </div>
      )}
      {data.today_bg && (
        <p className="text-[11px] font-bold mt-1.5" data-testid="chest-wall-today">{data.today_bg}</p>
      )}

      {open && (
        <div className="mt-4 space-y-4">
          {!data.enrolled ? (
            <button onClick={() => post("start", {}, "Програмата започна.")} disabled={busy || data.red_flag}
                    data-testid="chest-wall-start"
                    className="w-full py-2 rounded-lg bg-teal-600 text-white text-[11px] font-bold disabled:opacity-60">
              {busy ? "Стартирам…" : "Започнете програмата"}
            </button>
          ) : null}

          {/* Сесия с болка преди и след */}
          {data.enrolled && (
            <div className={`p-2.5 rounded-xl space-y-2 ${box}`} data-testid="chest-wall-session-form">
              <div className="text-[11px] font-bold">Сесия: болка преди и след</div>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] opacity-70">Преди 0-10</span>
                  <input type="number" min="0" max="10" className={inputCls} value={sess.before}
                         data-testid="chest-wall-pain-before"
                         onChange={(e) => setSess({ ...sess, before: e.target.value })} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] opacity-70">След 0-10</span>
                  <input type="number" min="0" max="10" className={inputCls} value={sess.after}
                         data-testid="chest-wall-pain-after"
                         onChange={(e) => setSess({ ...sess, after: e.target.value })} />
                </label>
              </div>
              <button onClick={logSession} disabled={busy} data-testid="chest-wall-session-save"
                      className="w-full py-2 rounded-lg bg-teal-600 text-white text-[11px] font-bold disabled:opacity-60">
                {busy ? "Записвам…" : "Запишете сесията"}
              </button>
            </div>
          )}

          {/* Отчет */}
          <div className={`p-3 rounded-xl border ${isDark ? tone.dark : tone.light}`} data-testid="chest-wall-outcome">
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-bold" data-testid="chest-wall-outcome-label">{out.label_bg}</span>
              {out.avg_delta != null && (
                <span className="ml-auto text-[10px] opacity-70 tabular-nums" data-testid="chest-wall-avg-delta">
                  −{out.avg_delta} т./сесия · {out.sessions} сесии
                </span>
              )}
            </div>
            <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{out.text_bg}</div>
            {out.first_week_before != null && out.last_week_before != null && (
              <div className="text-[10px] opacity-70 mt-1.5" data-testid="chest-wall-trend">
                Болка преди началото: седмица 1 → {out.first_week_before} · последна → {out.last_week_before}
              </div>
            )}
          </div>

          {chartData.length > 1 && (
            <div data-testid="chest-wall-chart">
              <div className="text-[11px] font-bold mb-1">Болка преди срещу след</div>
              <div style={{ width: "100%", height: 170 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={{ top: 6, right: 8, left: -22, bottom: 0 }}>
                    <CartesianGrid stroke={isDark ? "#1e293b" : "#e2e8f0"} strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="i" tick={{ fontSize: 9, fill: isDark ? "#94a3b8" : "#475569" }} tickLine={false} />
                    <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]}
                           tick={{ fontSize: 9, fill: isDark ? "#94a3b8" : "#475569" }} tickLine={false} />
                    <Tooltip contentStyle={{
                      background: isDark ? "#0f172a" : "#fff", borderRadius: 10, fontSize: 11,
                      border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                    }} labelFormatter={(l) => `Сесия ${l}`} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Line type="monotone" dataKey="преди" stroke="#f43f5e" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="след" stroke="#14b8a6" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Седмиците */}
          <div className="space-y-2" data-testid="chest-wall-weeks">
            {data.weeks.map((w) => (
              <div key={w.n} className={`p-3 rounded-xl ${box} ${w.current ? "ring-1 ring-teal-500" : ""}`}
                   data-testid={`chest-wall-week-${w.n}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold flex-1">
                    Седмица {w.n} · {w.title_bg}
                  </span>
                  {w.locked && <Lock className="w-3 h-3 opacity-50" />}
                  {data.enrolled && !w.locked && (
                    <span className="text-[10px] opacity-60 tabular-nums">{w.percent}%</span>
                  )}
                </div>
                <div className="text-[10px] opacity-75 mt-1 leading-relaxed">{w.focus_bg}</div>
                <div className="mt-2 space-y-1">
                  {w.habits.map((h) => (
                    <div key={`${w.n}-${h.key}`} className="flex items-start gap-2">
                      {data.enrolled && !w.locked ? (
                        <button onClick={() => post("checkin", { habit_key: h.key, done: !h.done_today })}
                                disabled={busy} data-testid={`chest-wall-habit-${w.n}-${h.key}`}
                                aria-checked={!!h.done_today} role="checkbox"
                                className={`shrink-0 w-4 h-4 mt-0.5 rounded border flex items-center justify-center ${h.done_today
                                  ? "bg-teal-600 border-teal-600" : isDark ? "border-slate-600" : "border-slate-400"}`}>
                          {h.done_today && <Check className="w-3 h-3 text-white" />}
                        </button>
                      ) : (
                        <span className="shrink-0 w-4 h-4 mt-0.5 rounded border opacity-30" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-bold leading-snug">{h.title_bg}</div>
                        <div className="text-[10px] opacity-75 leading-snug">{h.how_bg}</div>
                        {h.why_bg && (
                          <>
                            <button onClick={() => setWhy(why === `${w.n}-${h.key}` ? null : `${w.n}-${h.key}`)}
                                    className="text-[10px] opacity-50 hover:opacity-100"
                                    data-testid={`chest-wall-why-${w.n}-${h.key}`}>защо?</button>
                            {why === `${w.n}-${h.key}` && (
                              <div className="text-[10px] opacity-80 mt-0.5 leading-relaxed">{h.why_bg}</div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className={`p-3 rounded-xl ${isDark ? "bg-amber-900/20 border border-amber-500/30" : "bg-amber-50 border border-amber-200"}`}
               data-testid="chest-wall-safety">
            <div className="text-[11px] font-bold">Безопасност</div>
            <ul className="text-[10px] opacity-85 mt-1 space-y-0.5">
              {data.safety_bg.map((s, i) => <li key={i}>• {s}</li>)}
            </ul>
          </div>

          {data.links && (
            <div className="flex flex-wrap gap-1.5" data-testid="chest-wall-links">
              {data.links.map((l, i) => (
                <button key={i} onClick={() => navigate(l.route)} data-testid={`chest-wall-link-${i}`}
                        className={`text-[10px] px-2 py-1 rounded-lg border flex items-center gap-1 ${isDark ? "border-slate-700" : "border-slate-300"}`}>
                  {l.twin_bg} <ArrowRight className="w-3 h-3" /> <span className="opacity-70">{l.why_bg}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChestWallProgramCard;
